/**
 * Wishlist Guru hearts on Fitfindo cards (EU collection + search).
 *
 * Fitfindo renders `.ff-card` in the browser, so the app's collection icons
 * never see them. Each card gets the app's documented custom-code placeholder
 * (`.wf-wishlist` with product-id / product-handle / variant-id) and the app
 * draws the icon into it, picking new placeholders up on its own 3s poll.
 * Fitfindo replaces the cards on every filter/sort/page, so this is idempotent.
 * A swatch stays on the same product, and like AU the wishlist is per product
 * (variant wishlist off), so the heart needs no update on fitfindo:swatch-selected.
 *
 * The app fetches /<locale>/products/<handle>.js on click, and that 404s for the
 * untranslated handle Fitfindo links to, so nothing gets saved. Handles come from
 * templates/collection.wishlist-handles.liquid, rendered in the buyer's language.
 *
 * Hearts follow the app's own Collection Icons embed (theme editor > App embeds):
 * with it off, as on AU, the cards get no hearts. The embed loads collection-icon.js;
 * its absence is the switch, so check this if the hearts vanish after an app update.
 */
(function () {
  if (!document.querySelector('script[src*="wishlist-guru"][src*="/collection-icon.js"]')) return;

  var WRAPPER = 'ff-wishlist';
  var HANDLES_TTL = 60 * 60 * 1000;
  var handlesPromise;

  function fetchHandlesPage(url, page) {
    return fetch(url + '&page=' + page, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('wishlist handles ' + r.status);
      return r.json();
    });
  }

  function fetchHandles(url) {
    return fetchHandlesPage(url, 1).then(function (first) {
      var rest = [];
      for (var p = 2; p <= (first.pages || 1); p++) rest.push(fetchHandlesPage(url, p));
      return Promise.all(rest).then(function (pages) {
        return Object.assign.apply(Object, [{}].concat([first].concat(pages).map(function (pg) {
          return pg.handles || {};
        })));
      });
    });
  }

  function loadHandles() {
    if (handlesPromise) return handlesPromise;
    var url = window.ffWishlistHandlesUrl;
    if (!url) return (handlesPromise = Promise.resolve({}));
    var key = 'ff-wishlist-handles:' + url;
    try {
      var cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < HANDLES_TTL) return (handlesPromise = Promise.resolve(cached.handles));
    } catch (e) {}
    handlesPromise = fetchHandles(url)
      .then(function (handles) {
        try {
          sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), handles: handles }));
        } catch (e) {}
        return handles;
      })
      .catch(function () {
        return {};
      });
    return handlesPromise;
  }

  function handleFromCard(card) {
    var link = card.querySelector('.ff-card-link');
    var match = ((link && link.getAttribute('href')) || '').match(/\/products\/([^/?#]+)/);
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  // Same host as collection-card-badges.js, whichever script gets there first.
  function ensureHost(card) {
    var host = card.querySelector('.ff-badge-host');
    if (host) return host;

    host = document.createElement('div');
    host.className = 'ff-badge-host';

    var img = card.querySelector('.ff-card-img');
    if (img && img.parentNode) {
      img.parentNode.insertBefore(host, img);
      host.appendChild(img);
    } else {
      card.insertBefore(host, card.firstChild);
    }
    return host;
  }

  // The app's icon is a plain div, and the grid's click handler would follow
  // the card link. The wrapper gives it a 44px target, keyboard access and a
  // stop before the grid. The app's inline handler has already run by then.
  function pressIcon(wrapper) {
    var icon = wrapper.querySelector('.wf-wishlist [onclick]');
    if (icon) icon.click();
  }

  function createWrapper(card, handles) {
    var wrapper = document.createElement('div');
    wrapper.className = WRAPPER;
    wrapper.setAttribute('role', 'button');
    wrapper.setAttribute('tabindex', '0');
    wrapper.setAttribute('aria-label', window.ffWishlistLabel || 'Wishlist');

    var placeholder = document.createElement('div');
    placeholder.className = 'wf-wishlist';
    // Fitfindo's naming: data-group-id is the Shopify product, data-product-id its variant.
    placeholder.setAttribute('product-id', card.dataset.groupId);
    placeholder.setAttribute('product-handle', handles[card.dataset.groupId] || handleFromCard(card));
    placeholder.setAttribute('variant-id', card.dataset.productId);
    wrapper.appendChild(placeholder);

    wrapper.addEventListener('click', function (e) {
      e.stopPropagation();
      if (e.target === wrapper) pressIcon(wrapper);
    });
    wrapper.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      pressIcon(wrapper);
    });
    return wrapper;
  }

  function decorate(root) {
    var scope = root && root.querySelectorAll ? root : document;
    loadHandles().then(function (handles) {
      scope.querySelectorAll('.ff-card').forEach(function (card) {
        if (!card.dataset.groupId || card.querySelector('.' + WRAPPER)) return;
        ensureHost(card).appendChild(createWrapper(card, handles));
      });
    });
  }

  document.addEventListener('fitfindo:rendered', function (e) {
    decorate((e.detail && e.detail.grid) || document);
  });

  // Handle the case where Fitfindo rendered before this deferred script ran.
  if (document.querySelector('.ff-card')) {
    decorate(document);
  }
})();
